import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import customDropzoneStyles from "./CustomDropzone.styles";
import { openErrorNotification } from "helpers/methods";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import InfoHelper from "../InfoHelperText/InfoHelper";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import UploadIcon from "@mui/icons-material/Upload";
import { selectScreen } from "redux/appSlice";

/**
 * Why the dropzone turned a file away:
 * - `size`: over `maxFileSize`
 * - `format`: extension not in `acceptedFiles`
 * - `duplicate`: its name is already in `fileNames`
 * - `limit`: the document limit (`isUploadDocLimit` + `uploadDocLimit`) left no room for it
 */
export type DropzoneRejectionReason = "size" | "format" | "limit" | "duplicate";

export interface DropzoneRejection {
  file: File;
  reason: DropzoneRejectionReason;
}

interface CustomProps {
  sx?: any;
  innerBox?: any;
  dropzoneText?: string;
  dropzoneTextStyles?: any;
  icon?: any;
  onAdd: Function;
  /**
   * Called once per drop or browse that turned files away, with every rejected
   * file and its reason, before `onAdd` gets the accepted ones. When given, the
   * caller owns the feedback and the dropzone shows no error toast for these
   * rejections; when absent, the toasts are unchanged.
   */
  onReject?: (rejections: DropzoneRejection[]) => void;
  maxFileSize: number;
  acceptedFiles: string[];
  showAlerts: boolean;
  fileNames?: string[];
  uniqueId?: string | number;
  multipleFileUpload: boolean;
  uploadDocLimit?: number;
  disabled?: boolean;
  innerBody?: JSX.Element | ((state: { isDragOver: boolean }) => JSX.Element);
  dragOverSx?: any;
  disabledMessage?: string;
  fileExtenstionText?: string;
  isUploadDocLimit?: boolean;
  /**
   * Accessible name for the drop surface / hidden file input. Falls back to
   * `dropzoneText` and then a generic label, so screen-reader users always hear
   * a meaningful name for the control.
   */
  ariaLabel?: string;
}

const CustomDropzone = (props?: CustomProps) => {
  const classes = customDropzoneStyles;
  const theme = useTheme();
  const isMobile = useAppSelector(selectScreen);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  let { signRequest } = useAppSelector((state: RootState) => state.app);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const fileSizeAccepted = props?.maxFileSize
    ? props.maxFileSize
    : 10 * 1024 * 1024;
  // Caller `sx` overrides the defaults, not the other way round — otherwise a
  // caller's border/radius is silently dropped. `classes.dragover` is still
  // spread last at render time so drag feedback always wins.
  const appliedClass = props?.sx
    ? { ...classes.dropzone, ...props.sx }
    : classes.dropzone;
  const disabled = props?.disabled ?? false;
  const fileSizeInMB = fileSizeAccepted / (1024 * 1024);

  const useUploadDocLimit = props?.isUploadDocLimit ?? false;

  // Accessible name for the control regardless of which visual variant renders.
  const dropzoneAriaLabel =
    props?.ariaLabel ??
    (typeof props?.dropzoneText === "string" && props.dropzoneText
      ? props.dropzoneText
      : "Upload files");

  // The default variant renders a real, focusable "Upload" button, so the drop
  // surface must NOT also be a button (that would nest interactive controls).
  // The `innerBody` variant has no built-in trigger, so there the surface itself
  // becomes the keyboard-operable control — its content is non-interactive.
  const surfaceIsButton = Boolean(props?.innerBody);

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleSurfaceKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!surfaceIsButton) return;
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      openFilePicker();
    }
  };

  const getRejectionReason = (
    file: File,
  ): Exclude<DropzoneRejectionReason, "limit"> | null => {
    if (file.size > fileSizeAccepted) {
      return "size";
    }

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!props?.acceptedFiles.includes(fileExtension)) {
      return "format";
    }

    if (props.fileNames && props.fileNames.includes(file.name)) {
      return "duplicate";
    }

    return null;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    setDragCounter((prev) => prev + 1);
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    setDragCounter((prev) => {
      const newCount = prev - 1;
      if (newCount <= 0) {
        setIsDragOver(false);
        return 0;
      }
      return newCount;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    // Reset drag state
    setIsDragOver(false);
    setDragCounter(0);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(Array.from(files));
    }
    e.target.value = "";
  };

  const processFiles = (files: File[]) => {
    const onReject = props?.onReject;
    let filesToProcess = files;
    let exceededFiles: File[] = [];
    if (useUploadDocLimit && props?.uploadDocLimit) {
      const currentFileCount = signRequest.files.length;
      const remainingSlots = props.uploadDocLimit - currentFileCount;
      if (remainingSlots <= 0) {
        if (onReject) {
          if (files.length > 0) {
            onReject(
              files.map((file): DropzoneRejection => ({ file, reason: "limit" })),
            );
          }
        } else {
          openErrorNotification(
            `Upload limit reached. You can upload a maximum of ${props.uploadDocLimit} documents.`,
          );
        }
        return;
      }

      filesToProcess = files.slice(0, remainingSlots);
      exceededFiles = files.slice(remainingSlots);
    }

    const validFiles: File[] = [];
    // In drop order; the files cut off by the document limit come last.
    const rejections: DropzoneRejection[] = [];

    filesToProcess.forEach((file) => {
      const reason = getRejectionReason(file);
      if (!reason) {
        validFiles.push(file);
        return;
      }
      rejections.push({ file, reason });
      if (reason === "duplicate" && !onReject) {
        openErrorNotification(
          `File [${file.name}] already exists. Please try a different file.`,
        );
      }
    });
    exceededFiles.forEach((file) => rejections.push({ file, reason: "limit" }));

    if (onReject) {
      // One call per drop, before the accepted files go to `onAdd`, and the
      // caller shows the feedback instead of the toast.
      if (rejections.length > 0) {
        onReject(rejections);
      }
    } else {
      const namesRejectedFor = (reason: DropzoneRejectionReason) =>
        rejections
          .filter((rejection) => rejection.reason === reason)
          .map((rejection) => rejection.file.name);
      const invalidFiles = namesRejectedFor("format");
      const fileSizeExceededFiles = namesRejectedFor("size");

      let errorMessage = "";

      if (
        useUploadDocLimit &&
        exceededFiles.length > 0 &&
        props?.uploadDocLimit
      ) {
        const currentFileCount = signRequest.files.length;
        const remainingSlots = props.uploadDocLimit - currentFileCount;
        errorMessage += `Only ${remainingSlots} file(s) can be uploaded due to the limit of ${props.uploadDocLimit} documents. ${exceededFiles.length} file(s) were ignored.\n`;
      }

      if (invalidFiles.length > 0) {
        errorMessage += `File(s) [${invalidFiles.join(
          ", ",
        )}] have an unsupported file format.\n`;
      }

      if (fileSizeExceededFiles.length > 0) {
        errorMessage += `File(s) [${fileSizeExceededFiles.join(
          ", ",
        )}] exceed the maximum allowed file size of ${fileSizeInMB}MB.`;
      }

      if (errorMessage) {
        openErrorNotification(errorMessage);
      }
    }

    if (validFiles.length > 0) {
      props?.onAdd(validFiles);
    }
  };

  React.useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Clear the highlighted "drag" state. Needed for cancelled drags where the
    // dropzone's own dragleave/drop never fire (file released outside the
    // dropzone, dragged out of the window, or drag aborted via Escape).
    const resetDragState = () => {
      setIsDragOver(false);
      setDragCounter(0);
    };

    const handleDocumentDragOver = (e: Event) => preventDefaults(e);

    const handleDocumentDrop = (e: Event) => {
      preventDefaults(e);
      resetDragState();
    };

    const handleDocumentDragLeave = (e: DragEvent) => {
      preventDefaults(e);
      // Only reset when the drag actually leaves the window (relatedTarget is
      // null / pointer past the viewport edge), not when moving between
      // elements inside the page.
      if (
        e.relatedTarget === null ||
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        resetDragState();
      }
    };

    document.addEventListener("dragenter", preventDefaults);
    document.addEventListener("dragover", handleDocumentDragOver);
    document.addEventListener("dragleave", handleDocumentDragLeave);
    document.addEventListener("drop", handleDocumentDrop);
    document.addEventListener("dragend", resetDragState);

    return () => {
      document.removeEventListener("dragenter", preventDefaults);
      document.removeEventListener("dragover", handleDocumentDragOver);
      document.removeEventListener("dragleave", handleDocumentDragLeave);
      document.removeEventListener("drop", handleDocumentDrop);
      document.removeEventListener("dragend", resetDragState);
    };
  }, []);

  return (
    <>
      <input
        id={`drag&drop${props?.uniqueId}`}
        ref={inputRef}
        aria-label={dropzoneAriaLabel}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileInputChange}
        multiple={props?.multipleFileUpload}
        accept={props?.acceptedFiles.join(",")}
        disabled={
          props?.disabled ||
          (useUploadDocLimit &&
            props?.uploadDocLimit !== undefined &&
            signRequest.files.length >= props?.uploadDocLimit)
        }
      />
      <label
        htmlFor={`drag&drop${props?.uniqueId}`}
        style={{ width: "100%" }}
        onClick={(e) => {
          if (props?.disabled) {
            e.preventDefault();
            if (props?.disabledMessage) {
              openErrorNotification(props.disabledMessage);
              return;
            }
            const currentFileCount = signRequest.files.length;
            if (!hasAccessTo(strings.resources.sign, strings.sendPermission)) {
              openErrorNotification(
                "Access is denied! Please contact the administrator for assistance.",
              );
            }
            if (
              useUploadDocLimit &&
              props?.uploadDocLimit !== undefined &&
              currentFileCount >= props?.uploadDocLimit
            ) {
              openErrorNotification(
                `Upload limit reached. You can upload a maximum of ${props?.uploadDocLimit} documents.`,
              );
            }
          }
        }}
      >
        <Box
          sx={{
            ...(isDragOver
              ? {
                  ...appliedClass,
                  ...(props?.dragOverSx ?? classes.dragover),
                }
              : appliedClass),
            ...(surfaceIsButton && {
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "2px",
              },
            }),
          }}
          {...(surfaceIsButton
            ? {
                role: "button",
                tabIndex: disabled ? -1 : 0,
                // WCAG 2.5.3: the surface shows its own prompt, so its name
                // comes from that visible text unless a caller names it.
                "aria-label": props?.ariaLabel,
                "aria-disabled": disabled || undefined,
                onKeyDown: handleSurfaceKeyDown,
              }
            : {})}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {props?.innerBody ? (
            typeof props.innerBody === "function" ? (
              props.innerBody({ isDragOver })
            ) : (
              props.innerBody
            )
          ) : (
            <Box sx={{ ...classes.greyBox, ...props?.innerBox }}>
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={isMobile ? 1 : 1.5}
                p={isMobile ? 2 : 3}
              >
                {!isDragOver && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => {
                      if (disabled) return;
                      inputRef.current?.click();
                    }}
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      textTransform: "none",
                      borderRadius: "10px",
                      px: 2.5,
                      py: 1,
                      fontSize: isMobile ? "0.875rem" : "0.95rem",
                      fontWeight: 600,
                      boxShadow: "none",
                      cursor: disabled ? "not-allowed" : "pointer",
                    }}
                  >
                    Upload
                  </Button>
                )}
                <Box textAlign="center">
                  <Typography
                    fontSize={isMobile ? "1rem" : "1.25rem"}
                    sx={{
                      color: "text.primary",
                      fontWeight: 700,
                      lineHeight: 1.3,
                      letterSpacing: "-0.01em",
                      ...(props?.dropzoneTextStyles || {}),
                      ...(isDragOver && { color: "primary.main" }),
                    }}
                  >
                    {isDragOver ? "Drop your files here" : props?.dropzoneText}
                    {!isDragOver && (
                      <InfoHelper
                        title="Dropzone"
                        description="Drop your files here or click to upload. Supported formats are shown below. Maximum file size is based on your organization’s settings."
                        iconSize="small"
                        modal={isMobile}
                        tooltipPlacement="right"
                      />
                    )}
                  </Typography>
                  {!isDragOver && (
                    <Typography
                      sx={{
                        ...classes.acceptedFilesText,
                        mt: 0.5,
                        letterSpacing: "0.02em",
                      }}
                      fontSize={isMobile ? "0.7rem" : "0.85rem"}
                    >
                      {props?.fileExtenstionText
                        ? props?.fileExtenstionText
                        : `${props?.acceptedFiles
                            ?.map((file) =>
                              file.replace(".", "").toUpperCase(),
                            )
                            .join(", ")} up to ${fileSizeInMB} MB`}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </label>
    </>
  );
};
export default CustomDropzone;
